import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import * as auth from "../_redux/authRedux";
import {
  redirectToAuthFront,
  login,
  clearForceInteractive,
  isForceInteractiveLogin
} from "../_redux/authCrud";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { LayoutSplashScreen } from "../../../../_metronic/layout";
import useNewAuthServiceFlow from "../../../hooks/useNewAuthServiceFlow";

/*
  INTL (i18n) docs:
  https://github.com/formatjs/react-intl/blob/master/docs/Components.md#formattedmessage
*/

/*
  Formik+YUP:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
*/

const initialValues = {
  email: "",
  password: ""
};

function AuthNoticeBox({ children }) {
  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ minHeight: "100vh" }}
    >
      <div
        className="box-login"
        style={{
          border: "1px solid var(--border-neutral-subtle)",
          padding: "30px",
          borderRadius: "10px",
          maxWidth: "480px",
          width: "100%",
          background: "var(--bg-neutral-layer01)"
        }}
      >
        <div className="mb-8 alert alert-custom alert-light-danger alert-dismissible">
          <div className="alert-text font-weight-bold">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Login(props) {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [hasRedirectFailed, setHasRedirectFailed] = useState(false);
  const [redirectAttempt, setRedirectAttempt] = useState(0);
  const {
    isEnabled: isNewAuthServiceEnabled,
    isReady: isLoginFlowResolved
  } = useNewAuthServiceFlow();
  const authFrontRedirectStartedRef = useRef(false);

  const handleKeyDown = evt => {
    if (evt.key === "Enter") {
      formik.handleSubmit();
    }
  };
  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const hasLoginChallenge = new URLSearchParams(window.location.search).has(
    "login_challenge"
  );
  const hasLoginDestinationMisconfig =
    isNewAuthServiceEnabled && hasLoginChallenge;

  const startAuthFrontRedirect = async signal => {
    try {
      // After prompt=none → login_required, Callback sends ?prompt=login.
      const options = isForceInteractiveLogin()
        ? { prompt: "login" }
        : { prompt: "none" };
      if (options.prompt === "login") {
        clearForceInteractive();
      }
      const hasStarted = await Promise.resolve(redirectToAuthFront(options));

      if (!signal.aborted && !hasStarted) {
        setHasRedirectFailed(true);
      }
    } catch {
      if (!signal.aborted) {
        setHasRedirectFailed(true);
      }
    }
  };

  useEffect(() => {
    if (!isLoginFlowResolved) {
      return;
    }
    if (hasLoginDestinationMisconfig) {
      console.error(
        "[auth] login_challenge recebido no jusfy-admin; oauth_client_policy.login_destination deveria apontar para o auth-front"
      );
      return;
    }
    if (!isNewAuthServiceEnabled) {
      return;
    }
    if (authFrontRedirectStartedRef.current) {
      return;
    }
    authFrontRedirectStartedRef.current = true;

    const controller = new AbortController();

    startAuthFrontRedirect(controller.signal);

    return () => controller.abort();
  }, [
    isLoginFlowResolved,
    isNewAuthServiceEnabled,
    hasLoginDestinationMisconfig,
    redirectAttempt
  ]);

  const retryAuthFrontRedirect = () => {
    authFrontRedirectStartedRef.current = false;
    setHasRedirectFailed(false);
    setRedirectAttempt(attempt => attempt + 1);
  };

  const formik = useFormik({
    initialValues,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      enableLoading();
      setTimeout(() => {
        login(values.email, values.password)
          .then(({ data }) => {
            disableLoading();

            if (data.status === "require_2fa") {
              if (!data.accessToken || !data.userLoginId) {
                setSubmitting(false);
                setStatus(
                  "Erro ao processar a autenticação de dois fatores. Tente novamente."
                );
                return;
              }

              history.push("/auth/two-factor", {
                email: values.email,
                userLoginId: data.userLoginId,
                tempToken: data.accessToken
              });
              return;
            }

            props.login(data.accessToken);
          })
          .catch(err => {
            disableLoading();
            setSubmitting(false);

            if (err.response && err.response.data.error == "INVALID_LOGIN") {
              setStatus("E-mail ou senha estão incorretos");
            } else if (
              err.response &&
              err.response.data.error == "UNVERIFIED"
            ) {
              setStatus(
                "Você ainda não confirmou o link enviado para o seu e-mail. Verifique na caixa de spam."
              );
            } else {
              setStatus("Ocorreu um erro, por favor, tente novamente.");
            }
          });
      }, 1000);
    }
  });

  if (!isLoginFlowResolved) {
    return <LayoutSplashScreen />;
  }

  if (hasLoginDestinationMisconfig) {
    return (
      <AuthNoticeBox>
        Configuração de login incorreta — o destino de login deste cliente
        OAuth deveria ser o auth-front. Contate o suporte.
      </AuthNoticeBox>
    );
  }

  if (isNewAuthServiceEnabled && hasRedirectFailed) {
    return (
      <AuthNoticeBox>
        Não foi possível iniciar o login. Tente novamente.
        <div className="mt-5">
          <button className="btn btn-primary" onClick={retryAuthFrontRedirect}>
            TENTAR NOVAMENTE
          </button>
        </div>
      </AuthNoticeBox>
    );
  }

  if (isNewAuthServiceEnabled) {
    return <LayoutSplashScreen />;
  }

  return (
    <div
      className="jusfy-login"
      style={{
        background: `url(${toAbsoluteUrl(
          "/media/bg/bg-login.png"
        )}) no-repeat center center fixed`
      }}
    >
      <div className="head-login">
        <div className="container">
          <div className="logo">
            <img src={toAbsoluteUrl("/media/logos/logo-light.svg")} />
          </div>
        </div>
      </div>
      <div className="content-login">
        <div className="container">
          <div className="row" style={{ marginTop: "150px" }}>
            <div className="col-xl-5">
              <h1
                className="titulo"
                style={{ color: "var(--fg-neutral-subtler)", fontSize: "34px" }}
              >
                Todas as ferramentas que você precisa. Você encontra na{" "}
                <span className="primary">Jusfy</span>.
              </h1>
              <p
                className="subtitle"
                style={{
                  marginTop: "30px",
                  fontWeight: "500",
                  opacity: "0.8",
                  letterSpacing: "-0.01em",
                  color: "var(--fg-neutral-subtler)",
                  fontSize: "18px"
                }}
              >
                A maior e melhor ferramenta jurídica do Brasil.
              </p>
              <a className="saiba-mais" href="https://jusfy.com.br">
                <p
                  style={{
                    marginTop: "30px",
                    fontWeight: "600",
                    letterSpacing: "-0.01em",
                    color: "#41c78f",
                    fontSize: "18px"
                  }}
                >
                  SAIBA MAIS{" "}
                  <img
                    src={toAbsoluteUrl(
                      "/media/svg/icons/Navigation/ArrowRight.svg"
                    )}
                  />
                </p>
              </a>
            </div>
            <div className="col-xl-3"> </div>
            <div className="col-xl-4">
              <h1
                className="mb-10 login-entrar"
                style={{
                  fontSize: "29px",
                  color: "var(--fg-neutral-strong)",
                  letterSpacing: "-0.04em"
                }}
              >
                Entrar como admin
              </h1>
              <div
                className="box-login"
                style={{
                  border: "1px solid var(--border-neutral-subtle)",
                  padding: "30px",
                  borderRadius: "10px",
                  width: "100%"
                }}
              >
                {formik.status && (
                  <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                    <div className="alert-text font-weight-bold">
                      {formik.status}
                    </div>
                  </div>
                )}
                <p className="label-login">Digite seu e-mail</p>
                <input
                  style={{ marginBottom: "20px" }}
                  value={formik.values.email}
                  type="text"
                  className="form-control"
                  onChange={formik.handleChange}
                  name="email"
                  onKeyDown={handleKeyDown}
                />
                <p className="label-login">Digite sua senha</p>
                <input
                  name="password"
                  value={formik.values.password}
                  type="password"
                  style={{ marginBottom: "30px" }}
                  onChange={formik.handleChange}
                  className="form-control"
                  onKeyDown={handleKeyDown}
                />

                <div className="row">
                  <div className="col-xl-5">
                    <button
                      style={{ width: "100%" }}
                      className="btn btn-primary"
                      onClick={formik.handleSubmit}
                      disabled={formik.isSubmitting}
                    >
                      {loading && (
                        <span className="ml-3 spinner spinner-white"></span>
                      )}
                      ENTRAR
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default injectIntl(connect(null, auth.actions)(Login));
